import { useSnackbarController, useTranslation } from "@firecms/core";
import { Button, CircularProgress } from "@firecms/ui";
import { useCallback, useMemo, useRef, useState } from "react";
import { httpsCallable } from "firebase/functions";
import { useFirebaseFunctions } from "../hooks/useFirebaseFunctions";
import { getModeCollectionIds } from "../workspace/adminMode";
import { useAdminWorkspace } from "../workspace/useAdminWorkspace";
import { toCallableWorkspaceParams } from "../workspace/callableParams";
import { parseEventInvitationsCsv } from "../utils/parseEventInvitationsCsv";
import {
  AdminModal,
  adminModalFileInputClass,
} from "../custom/AdminModal";
import { useAdminTranslation } from "../i18n/useAdminTranslation";

type BulkInviteResponse = {
  invited: number;
  failed: { email: string; role: string; reason: string }[];
};

export function BulkInviteEventMembersButton() {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<BulkInviteResponse | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { mode } = useAdminWorkspace();
  const workspaceIds = useMemo(() => getModeCollectionIds(mode), [mode]);
  const functions = useFirebaseFunctions();
  const snackbarController = useSnackbarController();
  const { t } = useTranslation();
  const { t: ta } = useAdminTranslation();

  const eventReady = Boolean(
    workspaceIds.account?.accountId && workspaceIds.event?.eventId
  );

  const handleClose = useCallback(() => {
    if (!loading) {
      setOpen(false);
      setResult(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  }, [loading]);

  const handleFile = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file || !functions || !eventReady) {
        return;
      }

      const text = await file.text();
      const rows = parseEventInvitationsCsv(text);

      if (rows.length === 0) {
        snackbarController.open({
          message: ta("bulkInvite.invalidRows"),
          type: "warning",
        });
        return;
      }

      const callable = httpsCallable<
        Record<string, unknown>,
        BulkInviteResponse
      >(functions, "inviteEventParticipantsBulk");

      try {
        setLoading(true);
        setResult(null);
        const response = await callable({
          ...toCallableWorkspaceParams(workspaceIds),
          rows,
        });
        setResult(response.data);
        snackbarController.open({
          message: ta("bulkInvite.snackbarSummary", {
            invited: response.data.invited,
            failed: response.data.failed.length,
          }),
          type: response.data.failed.length > 0 ? "warning" : "success",
        });
      } catch (error: unknown) {
        const message =
          error instanceof Error ? error.message : ta("common.unknownError");
        snackbarController.open({
          message: ta("bulkInvite.importFailed", { message }),
          type: "error",
        });
      } finally {
        setLoading(false);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      }
    },
    [eventReady, functions, snackbarController, ta, workspaceIds]
  );

  return (
    <>
      <Button
        onClick={() => setOpen(true)}
        variant="outlined"
        disabled={!eventReady}
      >
        {ta("bulkInvite.button")}
      </Button>

      <AdminModal
        open={open}
        onClose={handleClose}
        ariaLabel={ta("bulkInvite.ariaLabel")}
        title={ta("bulkInvite.title")}
        description={ta("bulkInvite.description")}
        footer={
          <>
            {loading && <CircularProgress size="small" />}
            <Button onClick={handleClose} disabled={loading} variant="outlined">
              {t("close")}
            </Button>
          </>
        }
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,.tsv,.txt,text/csv,text/plain"
          disabled={loading}
          onChange={(e) => void handleFile(e)}
          className={adminModalFileInputClass}
        />

        {result && (
          <div className="mt-4 text-sm">
            <p className="font-medium text-slate-800 dark:text-slate-200">
              {ta("bulkInvite.summary", {
                invited: result.invited,
                failed: result.failed.length,
              })}
            </p>
            {result.failed.length > 0 && (
              <ul className="mt-2 max-h-48 space-y-1 overflow-y-auto text-slate-600 dark:text-slate-400">
                {result.failed.map((row, index) => (
                  <li key={`${row.email}-${row.role}-${index}`}>
                    {ta("bulkInvite.rowError", {
                      email: row.email,
                      role: row.role,
                      reason: row.reason,
                    })}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </AdminModal>
    </>
  );
}
